import { z } from 'zod';
import { ReferralAvivaPatientSchema } from './ReferralAvivaPatientSchema';
import { ReferralAvivaCaseManagerSchema } from './ReferralAvivaCaseManagerSchema';
import { ReferralAvivaEmployerSchema } from './ReferralAvivaEmployerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralAvivaDocumentsSchema } from './ReferralAvivaDocumentsSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

const ReferralBaseAvivaSchemaObject = {
  // ID is required for Aviva referrals to be passed from UI
  // because a referrer uploads documents to the storage "referrals/{generated-referral-id}/document.pdf"
  id: z.string().min(1),
  patient: ReferralAvivaPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
  caseManager: ReferralAvivaCaseManagerSchema,
  // Optional employer-side contact, captured so the EBP can email the employer (PPL-2639).
  employer: ReferralAvivaEmployerSchema,
  documents: ReferralAvivaDocumentsSchema,
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

export const ReferralCreateAvivaSchema = withLegacyPlanIdentifiers(
  z.object(ReferralBaseAvivaSchemaObject).check(requiresPatientPhoneForConsent),
);

// As it is stored in the database
export const ReferralAvivaSchema = ReferralBaseSchema.extend({
  ...ReferralBaseAvivaSchemaObject,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
});

export type ReferralCreateAviva = z.infer<typeof ReferralCreateAvivaSchema>;
export type ReferralAvivaData = z.infer<typeof ReferralAvivaSchema>;
export type ReferralAviva = IObject.HasIdTimestamped<ReferralAvivaData>;
